import { useEffect, useState } from "react";
import { Phone, Mail, MapPin, Facebook, Loader2, Check, Clock, Navigation } from "lucide-react";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import { scrollToId } from "@/lib/scroll";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INFO = [
  {
    icon: Phone,
    label: "Call us",
    value: "(352) 897-3160",
    href: "tel:+13528973160",
    testid: "contact-phone-link",
  },
  {
    icon: Mail,
    label: "Email",
    value: "Dreamworldsgarage@gmail.com",
    href: "mailto:Dreamworldsgarage@gmail.com",
    testid: "contact-email-link",
  },
  {
    icon: MapPin,
    label: "Find us",
    value: "3430 West Dunnellon Rd, Dunnellon, FL",
    href: "https://maps.google.com/?q=3430+West+Dunnellon+Rd,+Dunnellon,+FL",
    testid: "contact-address-link",
  },
  {
    icon: Facebook,
    label: "Facebook",
    value: "facebook.com/dreamworldsgarage",
    href: "https://www.facebook.com/dreamworldsgarage/",
    testid: "contact-facebook-link",
  },
];

const HOURS = [
  { days: "Monday – Friday", time: "10 AM – 6 PM" },
  { days: "Saturday", time: "Closed" },
  { days: "Sunday", time: "Closed" },
];

const inputCls =
  "w-full border border-bone/20 bg-ink px-4 py-3.5 text-base text-bone placeholder:text-bone/40 outline-none transition-colors duration-200 focus:border-steel";

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-bone/70">
      {label}
      {required && <span className="text-steel"> *</span>}
    </span>
    {children}
  </label>
);

export default function Contact() {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [openNow, setOpenNow] = useState(false);

  useEffect(() => {
    const tick = () => {
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: "America/New_York",
        weekday: "short",
        hour: "numeric",
        hour12: false,
      }).formatToParts(new Date());
      const wd = parts.find((p) => p.type === "weekday").value;
      const h = parseInt(parts.find((p) => p.type === "hour").value, 10);
      setOpenNow(!["Sat", "Sun"].includes(wd) && h >= 10 && h < 18);
    };
    tick();
    const t = setInterval(tick, 60000);
    return () => clearInterval(t);
  }, []);

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.detail || "Failed to send");
      setSent(true);
      setForm({ first_name: "", last_name: "", phone: "", email: "", message: "" });
      toast.success("Request received — we'll be in touch shortly.");
    } catch (err) {
      toast.error("Couldn't send your request. Please call us instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="border-t border-bone/10 bg-coal py-24 sm:py-32">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8">
        <div className="grid gap-14 lg:grid-cols-12 lg:gap-10">
          <Reveal className="lg:col-span-12">
            <p className="mb-6 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-bone/60 sm:text-sm">
              <span className="h-px w-10 bg-steel" />
              Schedule Service
            </p>
            <h2 className="font-display text-5xl font-black uppercase leading-[0.95] tracking-[0.02em] text-bone sm:text-7xl">
              Own the road —{" "}
              <span className="text-outline">book today.</span>
            </h2>
            <p className="mt-6 max-w-md text-lg leading-relaxed text-bone/85">
              Fill out the form and team up with our technicians. We&rsquo;ll
              get your car back on the road!
            </p>
          </Reveal>

          <Reveal className="lg:col-span-6">
            <div
              data-testid="business-hours-card"
              className="border border-bone/10 bg-charcoal p-6 sm:p-8"
            >
              <div className="flex items-center justify-between gap-4">
                <h3 className="flex items-center gap-3 font-display text-3xl font-black uppercase tracking-[0.02em] text-bone">
                  <Clock className="h-6 w-6 text-steel" />
                  Hours
                </h3>
                <span
                  data-testid="open-now-badge"
                  className={`flex items-center gap-2 border px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] ${
                    openNow
                      ? "border-steel text-bone"
                      : "border-bone/20 text-bone/50"
                  }`}
                >
                  <span
                    className={`h-2 w-2 rounded-full ${
                      openNow ? "animate-pulse bg-steel" : "bg-bone/30"
                    }`}
                  />
                  {openNow ? "Open now" : "Closed now"}
                </span>
              </div>
              <ul className="mt-5">
                {HOURS.map((h) => (
                  <li
                    key={h.days}
                    className="flex items-center justify-between border-b border-bone/10 py-3 last:border-b-0"
                  >
                    <span className="font-display text-xl font-semibold uppercase tracking-wide text-bone/80">
                      {h.days}
                    </span>
                    <span
                      className={`font-display text-xl font-bold uppercase tracking-wide ${
                        h.time === "Closed" ? "text-bone/40" : "text-bone"
                      }`}
                    >
                      {h.time}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-sm text-bone/70">
                Call anytime — (352) 897-3160
              </p>
            </div>

            <div className="mt-5 grid gap-px border border-bone/10 bg-bone/10">
              {INFO.map(({ icon: Icon, label, value, href, testid }) => (
                <a
                  key={testid}
                  href={href}
                  data-testid={testid}
                  target={href.startsWith("http") ? "_blank" : undefined}
                  rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
                  className="group flex min-h-[88px] items-center gap-4 bg-coal px-6 py-5 transition-colors duration-200 hover:bg-charcoal"
                >
                  <Icon className="h-5 w-5 shrink-0 text-steel" />
                  <span className="min-w-0">
                    <span className="block text-xs font-semibold uppercase tracking-[0.2em] text-bone/60">
                      {label}
                    </span>
                    <span className="mt-0.5 block break-words font-display text-xl font-semibold tracking-wide text-bone group-hover:text-white">
                      {value}
                    </span>
                  </span>
                </a>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-6 lg:self-center">
            {sent ? (
              <div
                data-testid="contact-success-message"
                className="flex h-full min-h-[420px] flex-col items-center justify-center border border-bone/10 bg-charcoal p-10 text-center"
              >
                <span className="flex h-16 w-16 items-center justify-center border-2 border-steel">
                  <Check className="h-8 w-8 text-steel" />
                </span>
                <h3 className="mt-8 font-display text-4xl font-black uppercase tracking-[0.02em] text-bone">
                  Request received.
                </h3>
                <p className="mt-3 max-w-sm text-base leading-relaxed text-bone/80">
                  Thanks for reaching out — our team will get back to you
                  shortly. Need it faster? Call (352) 897-3160.
                </p>
                <button
                  data-testid="contact-send-another-button"
                  onClick={() => setSent(false)}
                  className="mt-8 border border-bone/30 px-6 py-3 font-display text-lg font-bold uppercase tracking-wide text-bone transition-colors duration-200 hover:border-bone"
                >
                  Send another
                </button>
              </div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="contact-form"
                className="border border-bone/10 bg-charcoal p-6 sm:p-10"
              >
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="First Name" required>
                    <input
                      required
                      data-testid="contact-first-name-input"
                      className={inputCls}
                      value={form.first_name}
                      onChange={set("first_name")}
                      placeholder="First name"
                    />
                  </Field>
                  <Field label="Last Name" required>
                    <input
                      required
                      data-testid="contact-last-name-input"
                      className={inputCls}
                      value={form.last_name}
                      onChange={set("last_name")}
                      placeholder="Last name"
                    />
                  </Field>
                  <Field label="Phone" required>
                    <input
                      required
                      type="tel"
                      data-testid="contact-phone-input"
                      className={inputCls}
                      value={form.phone}
                      onChange={set("phone")}
                      placeholder="(352) 000-0000"
                    />
                  </Field>
                  <Field label="Email">
                    <input
                      type="email"
                      data-testid="contact-email-input"
                      className={inputCls}
                      value={form.email}
                      onChange={set("email")}
                      placeholder="you@email.com"
                    />
                  </Field>
                </div>
                <div className="mt-5">
                  <Field label="What's the dream?" required>
                    <textarea
                      required
                      rows={5}
                      data-testid="contact-message-input"
                      className={`${inputCls} resize-y`}
                      value={form.message}
                      onChange={set("message")}
                      placeholder="Tell us about your vehicle and what you need..."
                    />
                  </Field>
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit-button"
                  className="mt-8 flex w-full items-center justify-center gap-3 bg-navy px-8 py-4 font-display text-xl font-bold uppercase tracking-wide text-white transition-colors duration-200 hover:bg-bone hover:text-ink disabled:opacity-60"
                >
                  {sending ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    "Send Request"
                  )}
                </button>
              </form>
            )}
          </Reveal>
        </div>

        <Reveal delay={0.1} className="mt-14">
          <div
            data-testid="contact-map"
            className="relative overflow-hidden border border-bone/10"
          >
            <iframe
              title="Dreamworlds Garage — 3430 West Dunnellon Rd, Dunnellon, FL"
              src="https://www.google.com/maps?q=3430%20West%20Dunnellon%20Rd%2C%20Dunnellon%2C%20FL&output=embed"
              className="h-[320px] w-full sm:h-[420px]"
              style={{ filter: "grayscale(1) invert(0.9) contrast(0.9)" }}
              loading="lazy"
            />
            <a
              href="https://www.google.com/maps/dir/?api=1&destination=3430+West+Dunnellon+Rd,+Dunnellon,+FL"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-directions-link"
              className="absolute bottom-0 left-0 flex items-center gap-3 bg-ink/85 px-6 py-4 font-display text-lg font-semibold uppercase tracking-wide text-bone transition-colors duration-200 hover:text-white"
            >
              <Navigation className="h-4 w-4 text-steel" />
              Get directions
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
